import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Download, TrendingUp, Users, GraduationCap, Briefcase, FileCheck,
  ClipboardList, CheckCircle2, Award, Calendar
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Reports() {
  const [summary, setSummary] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [placement, setPlacement] = useState([]);
  const [batches, setBatches] = useState([]);
  const [batchFilter, setBatchFilter] = useState("all");

  const load = async () => {
    const params = {};
    if (batchFilter !== "all") params.batch = batchFilter;
    const [s, a, p, b] = await Promise.all([
      api.get("/reports/summary"),
      api.get("/reports/attendance", { params }),
      api.get("/reports/placement"),
      api.get("/batches"),
    ]);
    setSummary(s.data);
    setAttendance(a.data);
    setPlacement(p.data);
    setBatches(b.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [batchFilter]);

  const downloadCSV = async (kind) => {
    try {
      const token = localStorage.getItem("qie_token");
      const params = new URLSearchParams({ format: "csv" });
      if (kind === "attendance" && batchFilter !== "all") params.set("batch", batchFilter);
      const res = await fetch(`${BACKEND_URL}/api/reports/${kind}?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${kind}_report.csv`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`${kind} report downloaded`);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  if (!summary) return <div className="text-[#475569]">Loading reports...</div>;

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#64748B] mb-1">Analytics</div>
        <h1 className="font-heading text-3xl font-bold text-[#0F172A]">Reports & Analytics</h1>
        <p className="text-sm text-[#475569] mt-1">Generate institute-wide reports and export to CSV for further analysis.</p>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger">
        <KPI label="Total Students" value={summary.total_students} icon={Users} color="#0F2E4A" testid="kpi-students" />
        <KPI label="Active Batches" value={summary.total_batches} icon={GraduationCap} color="#059669" testid="kpi-batches" />
        <KPI label="Open Jobs" value={summary.open_jobs} icon={Briefcase} color="#D97706" testid="kpi-jobs" />
        <KPI label="Certificate Eligible" value={summary.certificate_eligible} icon={Award} color="#0F2E4A" testid="kpi-cert-eligible" />
        <KPI label="Applications" value={summary.total_applications} icon={FileCheck} color="#0F2E4A" testid="kpi-apps" />
        <KPI label="Interviews Scheduled" value={summary.total_interviews} icon={Calendar} color="#059669" testid="kpi-ivs" />
        <KPI label="Pending Leaves" value={summary.pending_leaves} icon={ClipboardList} color="#DC2626" testid="kpi-pending-leaves" />
        <KPI label="Approved Leaves" value={summary.approved_leaves} icon={CheckCircle2} color="#059669" testid="kpi-approved-leaves" />
      </div>

      {/* Attendance Report */}
      <div className="bg-white border border-[#E2E8F0] rounded-lg p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <h2 className="font-heading text-xl font-semibold text-[#0F172A]">Attendance Report</h2>
            <p className="text-xs text-[#64748B] mt-0.5">Per-student attendance summary. Students &ge; 85% are certificate-eligible.</p>
          </div>
          <div className="flex gap-3 items-center">
            <Select value={batchFilter} onValueChange={setBatchFilter}>
              <SelectTrigger className="w-48" data-testid="report-batch-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Batches</SelectItem>
                {batches.map((b) => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={() => downloadCSV("attendance")} data-testid="download-attendance-csv" className="bg-[#059669] hover:bg-[#047857] text-white">
              <Download className="w-4 h-4 mr-1.5" /> Export CSV
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC] text-left text-[11px] font-semibold uppercase tracking-wider text-[#475569]">
              <tr>
                <th className="px-3 py-2.5">ID</th>
                <th className="px-3 py-2.5">Name</th>
                <th className="px-3 py-2.5">Batch</th>
                <th className="px-3 py-2.5 text-right">Total</th>
                <th className="px-3 py-2.5 text-right">Present</th>
                <th className="px-3 py-2.5 text-right">Late</th>
                <th className="px-3 py-2.5 text-right">Half</th>
                <th className="px-3 py-2.5 text-right">Absent</th>
                <th className="px-3 py-2.5 text-right">%</th>
                <th className="px-3 py-2.5 text-right">Cert</th>
              </tr>
            </thead>
            <tbody data-testid="attendance-report-table">
              {attendance.length === 0 && <tr><td colSpan={10} className="text-center py-8 text-[#64748B]">No data.</td></tr>}
              {attendance.map((r) => (
                <tr key={r.student_id} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]">
                  <td className="px-3 py-2 font-mono-plex text-[#0F2E4A]">{r.student_id}</td>
                  <td className="px-3 py-2 font-medium">{r.name}</td>
                  <td className="px-3 py-2 text-[#475569]">{r.batch}</td>
                  <td className="px-3 py-2 text-right">{r.total_days}</td>
                  <td className="px-3 py-2 text-right text-[#059669]">{r.present}</td>
                  <td className="px-3 py-2 text-right text-[#D97706]">{r.late}</td>
                  <td className="px-3 py-2 text-right text-[#475569]">{r.half_day}</td>
                  <td className="px-3 py-2 text-right text-[#DC2626]">{r.absent}</td>
                  <td className="px-3 py-2 text-right font-semibold">
                    <span style={{ color: r.attendance_pct >= 85 ? "#059669" : r.attendance_pct >= 60 ? "#D97706" : "#DC2626" }}>
                      {r.attendance_pct}%
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {r.certificate_eligible === "Yes" ? (
                      <Award className="w-4 h-4 text-[#D97706] inline" />
                    ) : (
                      <span className="text-[#94A3B8]">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Placement Report */}
      <div className="bg-white border border-[#E2E8F0] rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-heading text-xl font-semibold text-[#0F172A]">Placement Report</h2>
            <p className="text-xs text-[#64748B] mt-0.5">All job applications across the institute.</p>
          </div>
          <Button onClick={() => downloadCSV("placement")} data-testid="download-placement-csv" className="bg-[#059669] hover:bg-[#047857] text-white">
            <Download className="w-4 h-4 mr-1.5" /> Export CSV
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC] text-left text-[11px] font-semibold uppercase tracking-wider text-[#475569]">
              <tr>
                <th className="px-3 py-2.5">Student</th>
                <th className="px-3 py-2.5">Company</th>
                <th className="px-3 py-2.5">Role</th>
                <th className="px-3 py-2.5">Applied</th>
                <th className="px-3 py-2.5">Status</th>
                <th className="px-3 py-2.5">Interview</th>
              </tr>
            </thead>
            <tbody data-testid="placement-report-table">
              {placement.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-[#64748B]">No applications yet.</td></tr>}
              {placement.map((r) => (
                <tr key={`${r.student_name}-${r.company}-${r.applied_on}`} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]">
                  <td className="px-3 py-2 font-medium">{r.student_name}</td>
                  <td className="px-3 py-2">{r.company}</td>
                  <td className="px-3 py-2 text-[#475569]">{r.role}</td>
                  <td className="px-3 py-2 text-[#64748B]">{r.applied_on}</td>
                  <td className="px-3 py-2">
                    <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-[#0F2E4A]/10 text-[#0F2E4A]">{r.application_status}</span>
                  </td>
                  <td className="px-3 py-2 text-[#475569]">
                    {r.interview_scheduled === "Yes" ? `${r.interview_date} ${r.interview_time}` : <span className="text-[#94A3B8]">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Batch Distribution */}
      <div className="bg-white border border-[#E2E8F0] rounded-lg p-6">
        <h2 className="font-heading text-xl font-semibold text-[#0F172A] mb-4">Batch Distribution</h2>
        <div className="space-y-3">
          {summary.by_batch.length === 0 && <div className="text-sm text-[#64748B]">No batches.</div>}
          {summary.by_batch.map((b) => (
            <div key={b.batch}>
              <div className="flex justify-between text-sm mb-1.5">
                <span className="font-medium text-[#0F172A]">{b.batch}</span>
                <span className="text-[#64748B]">{b.students} students</span>
              </div>
              <div className="h-2 bg-[#F1F5F9] rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#0F2E4A] to-[#059669] rounded-full transition-all"
                  style={{ width: `${Math.min(100, (b.students / Math.max(1, summary.total_students)) * 100)}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, icon: Icon, color, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-[#E2E8F0] rounded-lg p-5 hover:shadow-md transition-all">
      <div className="flex justify-between items-start">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] font-semibold">{label}</div>
          <div className="mt-2 font-heading text-2xl font-bold text-[#0F172A]">{value}</div>
        </div>
        <div className="w-9 h-9 rounded-md grid place-items-center" style={{ backgroundColor: `${color}12` }}>
          <Icon className="w-4.5 h-4.5" style={{ color }} strokeWidth={1.75} />
        </div>
      </div>
    </div>
  );
}
